import type { CountryCode } from 'libphonenumber-js'
import { useEffect, useRef } from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import PhoneInput from 'react-phone-number-input'
import 'react-phone-number-input/style.css'
import { splitInternationalPhone } from '@util/internationalPhone'
import './InternationalPhoneField.css'

type Props = {
  defaultCountry?: CountryCode
  label?: string
  name: string
}

type InputsProps = {
  defaultCountry?: CountryCode
  error?: string
  label: string
  onBlur?: () => void
  onChange: (value: string) => void
  required?: boolean
  value: string
}

export const InternationalPhoneInput = ({
  defaultCountry = 'NG',
  error,
  label,
  onBlur,
  onChange,
  required = true,
  value
}: InputsProps) => {
  const written = useRef<string | undefined>(undefined)

  useEffect(() => {
    if (!value || value.startsWith('+') || value === written.current) return

    const split = splitInternationalPhone(value, defaultCountry)
    if (!split.e164 || split.e164 === value) return

    written.current = split.e164
    onChange(split.e164)
  }, [defaultCountry, onChange, value])

  return (
    <div>
      <p className="mb-[2px] text-sm font-medium text-[#1f2937]">
        {label}
        {required ? (
          <>
            {' '}
            <span aria-hidden="true">*</span>
          </>
        ) : null}
      </p>
      <PhoneInput
        addInternationalOption={false}
        className="patient-phone"
        countryCallingCodeEditable={false}
        countryOptionsOrder={['NG', '|', '...']}
        defaultCountry={defaultCountry}
        international
        limitMaxLength
        onBlur={onBlur}
        onChange={next => {
          const stored = next || ''
          if (stored === value) return
          written.current = stored
          onChange(stored)
        }}
        value={value || undefined}
      />
      {error ? <p className="mt-1 pl-3 text-sm text-rose-600">{error}</p> : null}
    </div>
  )
}

export const InternationalPhoneField = ({ defaultCountry = 'NG', label = 'Phone Number', name }: Props) => {
  const { control } = useFormContext()

  return (
    <Controller
      control={control}
      name={name}
      render={({ field: { onBlur, onChange, value }, fieldState: { error } }) => (
        <InternationalPhoneInput
          defaultCountry={defaultCountry}
          error={typeof error?.message === 'string' ? error.message : undefined}
          label={label}
          onBlur={onBlur}
          onChange={onChange}
          value={typeof value === 'string' ? value : ''}
        />
      )}
    />
  )
}
